import { useRef, useEffect } from 'react';
import { Input } from '@mantine/core';
import {
    MDXEditor,
    MDXEditorMethods,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    thematicBreakPlugin,
    markdownShortcutPlugin,
    linkPlugin,
    linkDialogPlugin,
    toolbarPlugin,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    UndoRedo,
    BoldItalicUnderlineToggles,
    CreateLink,
    ListsToggle,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import './MarkdownEditor.css';

export interface MarkdownEditorProps {
    value?: string;
    onChange?: (value: string) => void;
    label?: React.ReactNode;
    description?: React.ReactNode;
    error?: React.ReactNode;
    placeholder?: string;
    minHeight?: number | string;
    withAsterisk?: boolean;
}

export function MarkdownEditor(props: MarkdownEditorProps) {
    const editorRef = useRef<MDXEditorMethods>(null);
    const lastValueRef = useRef<string>(props.value ?? '');

    useEffect(() => {
        if (props.value !== undefined && props.value !== lastValueRef.current) {
            lastValueRef.current = props.value;
            editorRef.current?.setMarkdown(props.value);
        }
    }, [props.value]);

    return (
        <Input.Wrapper
            label={props.label}
            description={props.description}
            error={props.error}
            withAsterisk={props.withAsterisk}
        >
            <div
                className="markdown-editor-wrapper"
                style={{
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    marginTop: '4px',
                    minHeight: props.minHeight ?? '120px',
                }}
            >
                <MDXEditor
                    ref={editorRef}
                    markdown={props.value ?? ''}
                    placeholder={props.placeholder}
                    onChange={(markdown) => {
                        lastValueRef.current = markdown;
                        props.onChange?.(markdown);
                    }}
                    plugins={[
                        headingsPlugin(),
                        listsPlugin(),
                        quotePlugin(),
                        thematicBreakPlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        markdownShortcutPlugin(),
                        diffSourcePlugin({ viewMode: 'rich-text' }),
                        toolbarPlugin({
                            toolbarContents: () => (
                                <DiffSourceToggleWrapper options={['rich-text', 'source']}>
                                    <UndoRedo />
                                    <BoldItalicUnderlineToggles />
                                    <CreateLink />
                                    <ListsToggle />
                                </DiffSourceToggleWrapper>
                            ),
                        }),
                    ]}
                />
            </div>
        </Input.Wrapper>
    );
}
